/**
 * Unified Feedback Provider
 * 
 * Combines error handling and toast notifications into a single provider
 * for simplified state management and reduced complexity.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Toast, 
  ToastAction, 
  ToastClose, 
  ToastDescription, 
  ToastProvider, 
  ToastTitle, 
  ToastViewport 
} from '@/components/ui/toast';

// Simplified error types
export enum ErrorType {
  API = 'api',           // All API and network related errors
  AUTH = 'auth',         // Authentication and authorization errors
  VALIDATION = 'validation', // Input validation errors
  SYSTEM = 'system'      // System and unexpected errors
}

// Simplified error interface
export interface AppError {
  type: ErrorType;
  message: string;
  details?: Record<string, any>;
  timestamp: Date;
}

// Toast types
export type ToastType = 'default' | 'destructive' | 'success' | 'warning' | 'info';

// Toast interface
export interface ToastNotification {
  id: string;
  title?: string;
  description: string;
  type: ToastType;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration: number;
}

// Unified feedback context
interface FeedbackContextType {
  // Error handling
  errors: AppError[];
  addError: (error: Omit<AppError, 'timestamp'>) => void;
  clearErrors: () => void;
  hasErrors: boolean;
  
  // Toast notifications
  toast: (props: {
    title?: string;
    description: string;
    type?: ToastType;
    action?: {
      label: string;
      onClick: () => void;
    };
    duration?: number;
  }) => void;
}

// Create unified context
const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

/**
 * FeedbackProvider Component
 * 
 * Unified provider for error handling and toast notifications
 */
export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<AppError[]>([]);
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  
  // Add a new error
  const addError = useCallback((error: Omit<AppError, 'timestamp'>) => {
    const newError: AppError = {
      ...error,
      timestamp: new Date(),
    };
    
    setErrors((prev) => [...prev, newError]);
    
    // Automatically show toast for errors
    showToast({
      title: getErrorTitle(error.type),
      description: error.message,
      type: error.type === ErrorType.VALIDATION ? 'warning' : 'destructive',
    });
  }, []);
  
  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);
  
  // Check if there are any errors
  const hasErrors = errors.length > 0;
  
  // Get error title based on type
  const getErrorTitle = (type: ErrorType): string => {
    switch (type) {
      case ErrorType.API:
        return 'Connection Error';
      case ErrorType.AUTH:
        return 'Authentication Error';
      case ErrorType.VALIDATION:
        return 'Validation Error';
      case ErrorType.SYSTEM:
        return 'System Error';
      default:
        return 'Error';
    }
  };
  
  // Show a toast notification
  const showToast = useCallback(({
    title,
    description,
    type = 'default',
    action,
    duration = 5000,
  }: {
    title?: string;
    description: string;
    type?: ToastType;
    action?: {
      label: string;
      onClick: () => void;
    };
    duration?: number;
  }) => {
    const id = Math.random().toString(36).substring(2, 9);
    
    const newToast: ToastNotification = {
      id,
      title,
      description,
      type,
      action,
      duration,
    };
    
    setToasts((prev) => [...prev, newToast]);
    
    // Remove toast after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);
  
  return (
    <FeedbackContext.Provider 
      value={{ 
        errors, 
        addError, 
        clearErrors, 
        hasErrors, 
        toast: showToast 
      }}
    >
      <ToastProvider>
        {children}
        
        {toasts.map(({ id, title, description, type, action }) => (
          <Toast key={id} variant={type}>
            {title && <ToastTitle>{title}</ToastTitle>}
            <ToastDescription>{description}</ToastDescription>
            
            {action && (
              <ToastAction altText={action.label} onClick={action.onClick}>
                {action.label}
              </ToastAction>
            )}
            
            <ToastClose />
          </Toast>
        ))}
        
        <ToastViewport />
      </ToastProvider>
    </FeedbackContext.Provider>
  );
};

/**
 * useFeedback Hook
 * 
 * Unified hook for error handling and toast notifications
 */
export const useFeedback = (): FeedbackContextType => {
  const context = useContext(FeedbackContext);
  
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  
  return context;
};

/**
 * API Error Handler
 * 
 * Simplified utility function to handle API errors
 */
export const handleApiError = (error: any, addError: FeedbackContextType['addError']): AppError => {
  let appError: Omit<AppError, 'timestamp'>;
  
  // Handle different error types with simplified logic
  if (error.response) {
    const status = error.response.status;
    
    if (status === 401 || status === 403) {
      appError = {
        type: ErrorType.AUTH,
        message: status === 401 
          ? 'Your session has expired. Please sign in again.' 
          : 'You do not have permission to perform this action.',
        details: error.response.data,
      };
    } else if (status === 400 || status === 422) {
      appError = {
        type: ErrorType.VALIDATION,
        message: 'Please check your input and try again.',
        details: error.response.data,
      };
    } else {
      appError = {
        type: ErrorType.API,
        message: error.response.data?.message || 'An error occurred while communicating with the server.',
        details: error.response.data,
      };
    }
  } else if (error.request) {
    // Request was made but no response received
    appError = {
      type: ErrorType.API,
      message: 'Unable to connect to the server. Please check your internet connection.',
      details: { request: error.request },
    };
  } else {
    // Error setting up the request or unexpected error
    appError = {
      type: ErrorType.SYSTEM,
      message: error.message || 'An unexpected error occurred.',
      details: { error },
    };
  }
  
  // Add error to context
  addError(appError);
  
  return {
    ...appError,
    timestamp: new Date(),
  };
};

export default {
  FeedbackProvider,
  useFeedback,
  handleApiError,
  ErrorType
};
